import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import * as THREE from "three";

import { cn } from "../../lib/utils";

function HeroNav({ brandLabel = "Woven" }) {
  return (
    <motion.nav
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { delay: 1, duration: 1 } }}
      className="absolute inset-x-0 top-0 z-20 p-6"
      aria-label="Hero"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="size-6 text-white" aria-hidden />
          <span
            className="text-xl font-bold text-white"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {brandLabel}
          </span>
        </div>
      </div>
    </motion.nav>
  );
}

function WovenCanvas() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    camera.position.z = 5;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio ?? 1, 2));

    const mouse = new THREE.Vector2(0, 0);
    const clock = new THREE.Clock();

    const mobile = typeof window.matchMedia === "function" && window.matchMedia("(max-width: 768px)").matches;
    const particleCount = mobile ? 14_000 : 50_000;

    const positions = new Float32Array(particleCount * 3);
    const originalPositions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    const velocities = new Float32Array(particleCount * 3);

    const geometry = new THREE.BufferGeometry();
    const torusKnot = new THREE.TorusKnotGeometry(1.5, 0.5, 200, 32);

    for (let i = 0; i < particleCount; i++) {
      const vertexIndex = i % torusKnot.attributes.position.count;
      const x = torusKnot.attributes.position.getX(vertexIndex);
      const y = torusKnot.attributes.position.getY(vertexIndex);
      const z = torusKnot.attributes.position.getZ(vertexIndex);

      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;
      originalPositions[i * 3] = x;
      originalPositions[i * 3 + 1] = y;
      originalPositions[i * 3 + 2] = z;

      const color = new THREE.Color();
      color.setHSL(Math.random(), 0.82, 0.62);

      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;

      velocities[i * 3] = 0;
      velocities[i * 3 + 1] = 0;
      velocities[i * 3 + 2] = 0;
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

    torusKnot.dispose();

    const material = new THREE.PointsMaterial({
      size: 0.021,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      transparent: true,
      opacity: 0.82,
      depthWrite: false,
    });

    const points = new THREE.Points(geometry, material);
    scene.add(points);

    const sizeRenderer = () => {
      const w = mount.clientWidth || 1;
      const h = mount.clientHeight || 1;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
    };

    mount.appendChild(renderer.domElement);
    sizeRenderer();

    const handleMouseMove = (event) => {
      const rect = mount.getBoundingClientRect();
      const px = rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0;
      const py = rect.height > 0 ? (event.clientY - rect.top) / rect.height : 0;
      mouse.x = px * 2 - 1;
      mouse.y = -(py * 2 - 1);
    };

    window.addEventListener("mousemove", handleMouseMove);

    let raf = 0;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      const elapsedTime = clock.getElapsedTime();

      const mx = mouse.x * 3;
      const my = mouse.y * 3;

      for (let i = 0; i < particleCount; i++) {
        const ix = i * 3;
        const px = positions[ix];
        const py = positions[ix + 1];
        const pz = positions[ix + 2];
        const ox = originalPositions[ix];
        const oy = originalPositions[ix + 1];
        const oz = originalPositions[ix + 2];

        let vx = velocities[ix];
        let vy = velocities[ix + 1];
        let vz = velocities[ix + 2];

        const dx = px - mx;
        const dy = py - my;
        const dz = pz;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

        if (dist < 1.5 && dist > 1e-6) {
          const force = ((1.5 - dist) * 0.01) / dist;
          vx += dx * force;
          vy += dy * force;
          vz += dz * force;
        }

        vx += (ox - px) * 0.001;
        vy += (oy - py) * 0.001;
        vz += (oz - pz) * 0.001;

        vx *= 0.95;
        vy *= 0.95;
        vz *= 0.95;

        velocities[ix] = vx;
        velocities[ix + 1] = vy;
        velocities[ix + 2] = vz;
        positions[ix] = px + vx;
        positions[ix + 1] = py + vy;
        positions[ix + 2] = pz + vz;
      }

      geometry.attributes.position.needsUpdate = true;

      points.rotation.y = elapsedTime * 0.05;
      renderer.render(scene, camera);
    };
    animate();

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => sizeRenderer())
        : null;
    ro?.observe(mount);

    const handleResize = () => sizeRenderer();
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
      ro?.disconnect();

      geometry.dispose();
      material.dispose();
      renderer.dispose();

      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div ref={mountRef} className="pointer-events-none absolute inset-0 z-0 min-h-[200px]" />;
}

/** Full-width hero with Three.js silk + headline; embed on Start Project or standalone demos. */
export function WovenLightHero({
  headline = "Woven by Light",
  subtitle = "An interactive tapestry of light and motion, crafted with code and creativity.",
  ctaLabel = "Explore the Weave",
  ctaHref = "#",
  eyebrow,
  eyebrowHref = "#",
  showInnerNav = false,
  innerNavBrand = "FuturoCode",
  className,
}) {
  const flattened = headline.replace(/\s+/g, " ").trim();
  const words = flattened.length ? flattened.split(" ") : [];
  let charIndex = 0;
  const totalChars = flattened.replace(/\s/g, "").length;
  const subtitleDelay = Math.min(2.25, 1.5 + totalChars * 0.035 + 0.15);
  const ctaDelay = subtitleDelay + 0.35;

  return (
    <section
      id="hero"
      className={cn(
        "relative flex min-h-[calc(100svh-4.375rem)] w-full flex-col items-center justify-center overflow-hidden rounded-b-xl",
        "bg-gradient-to-b from-neutral-950 via-black to-neutral-950",
        className,
      )}
      style={{
        fontFamily: "'Playfair Display', serif",
      }}
    >
      <WovenCanvas />
      {showInnerNav ? <HeroNav brandLabel={innerNavBrand} /> : null}
      <div className="relative z-10 w-full px-4 text-center">
        {eyebrow ? (
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="mb-4 text-sm font-semibold uppercase tracking-wide text-[#fff01a] [text-shadow:0_0_22px_rgba(255,240,26,0.45)] hover:[text-shadow:0_0_28px_rgba(255,250,120,0.55)]"
          >
            <a href={eyebrowHref} className="hover:underline hover:text-[#fff566]">
              {eyebrow}
            </a>
          </motion.p>
        ) : null}

        <h1
          className="text-balance text-5xl font-bold text-white md:text-7xl lg:text-8xl [&_*]:text-white"
          style={{
            fontFamily: "'Playfair Display', serif",
            color: "#ffffff",
            textShadow: "0 0 50px rgba(255, 255, 255, 0.25)",
          }}
        >
          {words.map((word, wi) => (
            <span key={`${word}-${wi}`} className="inline-block text-white">
              {word.split("").map((char, j) => {
                const idx = charIndex;
                charIndex += 1;
                return (
                  <motion.span
                    key={`${wi}-${j}-${char}`}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: 1.35 + idx * 0.04,
                      duration: 1.2,
                      ease: [0.2, 0.65, 0.3, 0.9],
                    }}
                    className="inline-block text-white"
                    style={{ display: "inline-block", color: "#ffffff" }}
                  >
                    {char}
                  </motion.span>
                );
              })}
              {wi < words.length - 1 ? <span>&nbsp;</span> : null}
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 1,
            delay: subtitleDelay,
            ease: [0.2, 0.65, 0.3, 0.9],
          }}
          className="mx-auto mt-6 max-w-2xl text-lg font-normal leading-relaxed text-neutral-100 md:text-xl"
          style={{
            fontFamily: "'Inter', sans-serif",
            textShadow:
              "0 0 40px rgba(0, 0, 0, 0.85), 0 2px 8px rgba(0, 0, 0, 0.9), 0 1px 0 rgba(0, 0, 0, 0.5)",
          }}
        >
          {subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, delay: ctaDelay }}
          className="mt-10"
        >
          <a
            href={ctaHref}
            className="inline-block rounded-full border-2 border-white/25 bg-white/10 px-8 py-3 font-semibold text-white backdrop-blur-sm transition-all hover:bg-white/[0.18] hover:border-white/35"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {ctaLabel}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
